import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  APPLE_TEAM_TYPES,
  getUserErrorMessage,
  mobileAppConfigStrictSchema,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { Info } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { AppleAppStoreInfo } from '@/components/AppleAppStoreInfo';
import { EditableVersionField } from '@/components/EditableVersionField';
import { ErrorAlert } from '@/components/ErrorAlert';
import { UploadButton } from '@/components/UploadButton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { NullableInput } from '@/components/ui+/NullableInput';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

const formValuesSchema = mobileAppConfigStrictSchema;
type FormValues = z.output<typeof formValuesSchema>;

export const Route = createFileRoute('/r/$restaurantId/s/mobile-app-config')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();
  const convex = useConvex();
  const {
    data: mobileAppConfig,
    error,
    isError,
    isPending,
  } = useQuery(api.mobileAppConfigs.getByRestaurantId, { restaurantId });

  const form = useForm({
    defaultValues: { expoAppleTeamType: 'IN_HOUSE' },
    resolver: zodResolver(formValuesSchema),
  });

  // Subscribe to changes
  useEffect(() => {
    if (!mobileAppConfig) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(mobileAppConfig, { keepDirty: true, keepDirtyValues: true });
  }, [mobileAppConfig]);

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.mobileAppConfigs.save, {
        mobileAppConfig: formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading mobile app config" />;
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Mobile App Config</CardTitle>
          <CardDescription>Build native iOS & Android consumer apps</CardDescription>
        </CardHeader>
        <CardContent>
          <FormWrapper {...form}>
            <form
              id="form1"
              onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
              className="grid gap-6"
            >
              {/* Display name */}
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* iOS Bundle ID */}
              <FormField
                control={form.control}
                name="iosBundleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>iOS Bundle ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple Merchant ID */}
              <FormField
                control={form.control}
                name="appleMerchantId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple Merchant ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Android Bundle ID */}
              <FormField
                control={form.control}
                name="androidBundleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Android Bundle ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple Team Type  */}
              <FormField
                control={form.control}
                name="expoAppleTeamType"
                render={({ field }) => (
                  <FormItem className="w-[280px]">
                    <FormLabel>Apple Team Type</FormLabel>
                    <Select defaultValue={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {APPLE_TEAM_TYPES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple Team ID */}
              <FormField
                control={form.control}
                name="expoAppleTeamId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple Team ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple App Store Connect Issuer ID */}
              <FormField
                control={form.control}
                name="expoAppleAscIssuerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple App Store Connect Issuer ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple App Store Connect Key ID */}
              <FormField
                control={form.control}
                name="expoAppleAscKeyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple App Store Connect Key ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple ID */}
              <FormField
                control={form.control}
                name="expoAppleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple ID</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple ID Password */}
              <FormField
                control={form.control}
                name="expoApplePassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple ID Password (encrypted)</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Apple App Specific Password */}
              <FormField
                control={form.control}
                name="expoAppleAppSpecificPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apple App Specific Password (encrypted)</FormLabel>
                    <FormControl>
                      <NullableInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Images (can only be added once initial config is set up) */}
              {!!mobileAppConfig?._id && (
                <div className={'flex gap-4 justify-end'}>
                  {!mobileAppConfig.iconImageStorageId ? (
                    <UploadButton
                      buttonText="Upload icon"
                      fileTypes={['.png']}
                      imageHeight={1024}
                      imageWidth={1024}
                      onUploadComplete={async (uploadResponse) => {
                        const storageId = uploadResponse[0]?.response.storageId;
                        if (!storageId) return;
                        await convex.mutation(api.mobileAppConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'icon',
                        });
                        toast.success('Icon uploaded successfully');
                      }}
                      onUploadError={(error: unknown) => {
                        console.error('upload error', error);
                        toast.error('Error uploading file!');
                      }}
                      // `uploadUrl` is only called after the user has selected a file to upload.
                      uploadUrl={() => convex.mutation(api.files.generateUploadUrl)}
                    />
                  ) : (
                    <div className="flex gap-2">
                      {mobileAppConfig.iconImageStorageUrl && (
                        <ActionButton
                          onClick={(e) => {
                            e.preventDefault();
                            if (mobileAppConfig.iconImageStorageUrl) {
                              window.open(mobileAppConfig.iconImageStorageUrl, '_blank');
                            }
                          }}
                          variant={'outline'}
                        >
                          View icon
                        </ActionButton>
                      )}
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await convex.mutation(api.mobileAppConfigs.deleteImage, {
                            restaurantId,
                            type: 'icon',
                          });
                        }}
                        variant={'destructive'}
                      >
                        Delete icon
                      </ActionButton>
                    </div>
                  )}

                  {!mobileAppConfig.bannerImageStorageId ? (
                    <UploadButton
                      buttonText="Upload banner image"
                      fileTypes={['.jpg']}
                      imageHeight={560}
                      imageWidth={750}
                      onUploadComplete={async (uploadResponse) => {
                        const storageId = uploadResponse[0]?.response.storageId;
                        if (!storageId) return;
                        await convex.mutation(api.mobileAppConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'banner',
                        });
                        toast.success('Banner uploaded successfully');
                      }}
                      onUploadError={(error: unknown) => {
                        console.error('upload error', error);
                        toast.error('Error uploading file!');
                      }}
                      uploadUrl={() => convex.mutation(api.files.generateUploadUrl)}
                    />
                  ) : (
                    <div className="flex gap-2">
                      {mobileAppConfig.bannerImageStorageUrl && (
                        <ActionButton
                          onClick={(e) => {
                            e.preventDefault();
                            if (mobileAppConfig.bannerImageStorageUrl) {
                              window.open(mobileAppConfig.bannerImageStorageUrl, '_blank');
                            }
                          }}
                          variant={'outline'}
                        >
                          View banner
                        </ActionButton>
                      )}
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await convex.mutation(api.mobileAppConfigs.deleteImage, {
                            restaurantId,
                            type: 'banner',
                          });
                        }}
                        variant={'destructive'}
                      >
                        Delete banner
                      </ActionButton>
                    </div>
                  )}
                </div>
              )}
            </form>
          </FormWrapper>
        </CardContent>
        <CardFooter className="border-t">
          <ActionButton
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            loading={form.formState.isSubmitting}
            type="submit"
            form="form1"
          >
            Save
          </ActionButton>
        </CardFooter>
      </Card>

      {/* App Versions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="mb-2">App Store Update Policies</CardTitle>
          <Alert className="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
            <Info className="h-4 w-4" />
            <AlertTitle>Update banners</AlertTitle>
            <AlertDescription>
              Apple App Store and Google Play Store versions should be kept in sync with these
              values. A notice will be shown to users when the version set here is greater than the
              version they have installed.
            </AlertDescription>
          </Alert>
        </CardHeader>
        <CardContent>
          <FormWrapper {...form}>
            <form
              id="form2"
              onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
              className="grid gap-4"
            >
              {/* Consumer Native App Settings */}
              <div className="space-y-4">
                <div className="space-y-4">
                  {/* Android Row */}
                  <EditableVersionField
                    control={form.control}
                    name="targetNativeVersionAndroid"
                    label="Android"
                    forceUpdateName="isUpdateRequiredAndroid"
                  />

                  {/* iOS Row */}
                  <div className="space-y-2">
                    <EditableVersionField
                      control={form.control}
                      name="targetNativeVersionIos"
                      label="iOS"
                      forceUpdateName="isUpdateRequiredIos"
                    />

                    {/* App Store Version Info */}
                    {mobileAppConfig?.iosBundleId && (
                      <AppleAppStoreInfo iosBundleId={mobileAppConfig.iosBundleId} />
                    )}
                  </div>
                </div>
              </div>
            </form>
          </FormWrapper>
        </CardContent>
        <CardFooter className="border-t">
          <ActionButton
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            loading={form.formState.isSubmitting}
            type="submit"
            form="form2"
          >
            Save
          </ActionButton>
        </CardFooter>
      </Card>
    </div>
  );
}
